import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  viewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CapturumButtonComponent } from '@capturum/ui/button';
import { DateTimeRangeValue } from '@core/dynamic-filters/interfaces/date-time-range-filter.interface';
import { GenericFormGroup } from '@core/interfaces/form.interface';
import { FieldType, FormlyFieldConfig, FormlyFieldProps } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { DateTimeRangeService } from '@shared/services/date-time-range.service';
import { DatePicker } from 'primeng/datepicker';
import { Popover } from 'primeng/popover';

interface AdditionalDefaultOptions {
  selectionMode: 'single' | 'range';
  minDate: Date | null;
  maxDate: Date | null;
  maxDateRangeInDays?: number | null;
  onlyToDate?: boolean;
}

@Component({
  selector: 'app-formly-date-time-range-type',
  imports: [FormsModule, ReactiveFormsModule, DatePicker, Popover, CapturumButtonComponent, TranslateModule, DatePipe],
  providers: [DateTimeRangeService],
  templateUrl: './formly-date-time-range-type.component.html',
  styleUrl: './formly-date-time-range-type.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FormlyDateTimeRangeTypeComponent extends FieldType implements OnInit {
  readonly dateTimeRangePicker = viewChild<Popover>('dateTimeRangePicker');

  formGroup: FormGroup<GenericFormGroup<DateTimeRangeValue>>;
  maxDate: WritableSignal<Date | null>;
  public override defaultOptions?: Partial<FormlyFieldConfig<FormlyFieldProps & AdditionalDefaultOptions>> = {
    props: {
      selectionMode: 'range',
      minDate: null,
      maxDate: null,
    },
  };

  readonly #formBuilder = inject(FormBuilder);
  readonly #dateTimeRangeService = inject(DateTimeRangeService);

  ngOnInit(): void {
    this.maxDate = signal(this.props.maxDate || null);

    this.formGroup = this.#formBuilder.group<GenericFormGroup<DateTimeRangeValue>>({
      date: this.#formBuilder.control(null),
      from: this.#formBuilder.control('00:00'),
      to: this.#formBuilder.control('00:00'),
    });

    if (this.formControl.value) {
      this.formGroup.patchValue(this.formControl.value);
    }
  }

  checkFromToTime(name: string): void {
    this.#dateTimeRangeService.checkFromToTime(name, this.formGroup);
  }

  toggle(event: MouseEvent): void {
    this.#dateTimeRangeService.toggleDatePicker(event, this.dateTimeRangePicker());
  }

  onDateSelect(date: Date): void {
    const dateValue = (this.formGroup.get('date')?.value as [Date, Date]) || [null, null];

    this.#dateTimeRangeService.onDateSelect(
      this.props.selectionMode,
      dateValue,
      this.props.maxDateRangeInDays,
      this.maxDate,
    );
  }

  apply(): void {
    const normalizedValue = this.#dateTimeRangeService.normalizeValue(
      this.formGroup.value,
      this.props.selectionMode,
      this.props.onlyToDate,
    );

    this.formControl.setValue(normalizedValue);
    this.dateTimeRangePicker().hide();
  }

  resetTime(): void {
    this.#dateTimeRangeService.resetTime(this.formGroup);
  }
}
